import React, { useEffect, useState } from "react";
import { LoadScript, GoogleMap, DirectionsService, DirectionsRenderer, Marker } from "@react-google-maps/api";

const OptimizedRouteMap = ({ deliveryPoints }) => {
  const [directions, setDirections] = useState(null);
  const [mapError, setMapError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [geocodedPoints, setGeocodedPoints] = useState([]);
  const [geocoder, setGeocoder] = useState(null);

  // Default center (Hyderabad)
  const defaultCenter = { lat: 17.385044, lng: 78.486671 };

  // Initialize geocoder when Google Maps loads
  useEffect(() => {
    if (window.google && window.google.maps) {
      setGeocoder(new window.google.maps.Geocoder());
    }
  }, []);

  // Geocode address to lat/lng
  const geocodeAddress = (address) => {
    return new Promise((resolve, reject) => {
      if (!geocoder) {
        reject(new Error("Geocoder not available"));
        return;
      }
      geocoder.geocode({ address }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const location = results[0].geometry.location;
          resolve({
            lat: location.lat(),
            lng: location.lng(),
            address: address
          });
        } else {
          reject(new Error(`Geocoding failed: ${status}`));
        }
      });
    });
  };

  // Process delivery points and geocode addresses
  useEffect(() => {
    const processDeliveryPoints = async () => {
      if (!deliveryPoints || deliveryPoints.length === 0) {
        setMapError(true);
        setLoading(false);
        return;
      }
      setLoading(true);
      setMapError(false);
      try {
        const processedPoints = [];
        for (const point of deliveryPoints) {
          if (point.lat && point.lng) {
            processedPoints.push({
              lat: point.lat,
              lng: point.lng,
              label: point.label || 'Delivery Point',
              address: point.address || ''
            });
          } else if (point.address) {
            try {
              const geocoded = await geocodeAddress(point.address);
              if (geocoded) {
                processedPoints.push({
                  ...geocoded,
                  label: point.label || 'Delivery Point',
                  address: point.address
                });
              }
            } catch (error) {
              processedPoints.push({
                address: point.address,
                label: point.label || 'Delivery Point',
                geocodeFailed: true
              });
            }
          } else if (typeof point === 'string') {
            try {
              const geocoded = await geocodeAddress(point);
              if (geocoded) {
                processedPoints.push({
                  ...geocoded,
                  label: 'Delivery Point',
                  address: point
                });
              }
            } catch (error) {
              processedPoints.push({
                address: point,
                label: 'Delivery Point',
                geocodeFailed: true
              });
            }
          }
        }
        setGeocodedPoints(processedPoints);
      } catch (error) {
        setMapError(true);
      } finally {
        setLoading(false);
      }
    };
    if (geocoder || deliveryPoints?.some(p => p.lat && p.lng)) {
      processDeliveryPoints();
    }
  }, [deliveryPoints, geocoder]);

  const directionsCallback = (response) => {
    if (response !== null) {
      if (response.status === "OK") {
        setDirections(response);
        setMapError(false);
      } else {
        setMapError(true);
      }
    }
  };

  if (loading) {
    return (
      <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
        <h2 className="text-xl font-bold mb-2 text-green-700">🗺️ Loading Map...</h2>
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Processing delivery locations...</p>
        </div>
      </div>
    );
  }

  // Error fallback component
  const ErrorFallback = () => (
    <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
      <h2 className="text-xl font-bold mb-2 text-red-700">🗺️ Map Error</h2>
      <div className="text-center py-8">
        <div className="text-4xl mb-4">⚠️</div>
        <p className="text-gray-600 mb-2">
          {!deliveryPoints || deliveryPoints.length === 0
            ? "No delivery locations available"
            : "Unable to load map or geocode addresses"
          }
        </p>
      </div>
    </div>
  );

  if (mapError || !deliveryPoints || deliveryPoints.length === 0) {
    return <ErrorFallback />;
  }

  // Get valid coordinates for map operations
  const validCoordinates = geocodedPoints.filter(p => p.lat && p.lng);

  // For single delivery point, show simple map with marker
  if (deliveryPoints.length === 1 && validCoordinates.length === 1) {
    const point = validCoordinates[0];
    return (
      <LoadScript
        googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
        libraries={["places"]}
        onError={() => setMapError(true)}
      >
        <div>
          <h2 className="text-xl font-bold mb-2 text-green-700">🗺️ Delivery Location</h2>
          <GoogleMap
            mapContainerStyle={{
              width: "100%",
              height: "500px",
              borderRadius: "12px",
            }}
            center={{ lat: point.lat, lng: point.lng }}
            zoom={16}
            options={{
              mapTypeControl: true,
              streetViewControl: true,
              fullscreenControl: true,
            }}
          >
            <Marker
              position={{ lat: point.lat, lng: point.lng }}
              title={point.label}
              label={point.label}
            />
          </GoogleMap>
        </div>
      </LoadScript>
    );
  }

  // For multiple delivery points, show optimized route
  if (validCoordinates.length >= 2) {
    const origin = validCoordinates[0];
    const destination = validCoordinates[validCoordinates.length - 1];
    const waypoints = validCoordinates.slice(1, -1).map((point) => ({
      location: { lat: point.lat, lng: point.lng },
      stopover: true,
    }));

    return (
      <LoadScript
        googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
        libraries={["places"]}
        onError={() => setMapError(true)}
      >
        <div>
          <h2 className="text-xl font-bold mb-2 text-green-700">🗺️ Optimized Route</h2>
          <GoogleMap
            mapContainerStyle={{
              width: "100%",
              height: "500px",
              borderRadius: "12px",
            }}
            center={defaultCenter}
            zoom={12}
            options={{
              mapTypeControl: true,
              streetViewControl: true,
              fullscreenControl: true,
            }}
          >
            <DirectionsService
              options={{
                origin: { lat: origin.lat, lng: origin.lng },
                destination: { lat: destination.lat, lng: destination.lng },
                waypoints,
                travelMode: window.google?.maps?.TravelMode?.DRIVING || "DRIVING",
                optimizeWaypoints: true,
              }}
              callback={directionsCallback}
            />
            {directions && (
              <DirectionsRenderer
                options={{
                  directions,
                  suppressMarkers: false,
                  polylineOptions: {
                    strokeColor: "#16a34a",
                    strokeWeight: 4,
                  },
                }}
              />
            )}
          </GoogleMap>
        </div>
      </LoadScript>
    );
  }

  // Fallback for addresses that couldn't be geocoded
  return <ErrorFallback />;
};

export default OptimizedRouteMap;